import { distance } from 'fastest-levenshtein';

/**
 * Canonical names for common skills (curated list for critical cases)
 * Much smaller than before - only for acronyms, special characters, etc.
 */
const CANONICAL_SKILLS: Record<string, string> = {
	// Common acronyms
	'js': 'JavaScript',
	'ts': 'TypeScript',
	'k8s': 'Kubernetes',
	'aws': 'AWS',
	'gcp': 'Google Cloud Platform',
	
	// Special characters
	'c#': 'C#',
	'csharp': 'C#',
	'c++': 'C++',
	'cpp': 'C++',
	'.net': '.NET',
	'dotnet': '.NET',
	'asp.net': 'ASP.NET',
	'aspnet': 'ASP.NET',
	
	// Common variations
	'nodejs': 'Node.js',
	'node.js': 'Node.js',
	'reactjs': 'React',
	'react.js': 'React',
	'postgresql': 'PostgreSQL',
	'postgres': 'PostgreSQL',
	'monday.com': 'Monday.com',
	'monday': 'Monday.com',
};

/**
 * Cache for skill normalizations
 */
const normalizationCache = new Map<string, string>();

/**
 * Maximum Levenshtein distance threshold
 */
const FUZZY_MATCH_THRESHOLD = 2;

/**
 * Minimum similarity ratio (0-1)
 */
const SIMILARITY_THRESHOLD = 0.7;

/**
 * Normalizes skill names using fuzzy string matching (Levenshtein distance)
 * 
 * @param skill - The skill name to normalize
 * @param knownSkills - Optional array of known skills to match against
 * @returns The normalized skill name
 */
export const normalizeSkill = (skill: string, knownSkills: string[] = []): string => {
	if (!skill) return skill;
	
	const trimmed = skill.trim();
	if (!trimmed) return trimmed;
	
	// Check cache
	if (normalizationCache.has(trimmed.toLowerCase())) {
		return normalizationCache.get(trimmed.toLowerCase())!;
	}
	
	// Handle .NET with version numbers
	if (/^\.net\s*(\d+\.?\d*)?$/i.test(trimmed)) {
		const version = trimmed.match(/^\.net\s*(\d+\.?\d*)?$/i)?.[1];
		const normalized = version ? `.NET ${version}` : '.NET';
		normalizationCache.set(trimmed.toLowerCase(), normalized);
		return normalized;
	}
	
	const lowerSkill = trimmed.toLowerCase();
	
	// Check canonical list first
	if (CANONICAL_SKILLS[lowerSkill]) {
		const canonical = CANONICAL_SKILLS[lowerSkill];
		normalizationCache.set(lowerSkill, canonical);
		return canonical;
	}
	
	// Try fuzzy matching against known skills
	if (knownSkills.length > 0) {
		let bestMatch = trimmed;
		let bestDistance = Infinity;
		let bestSimilarity = 0;
		
		for (const knownSkill of knownSkills) {
			const dist = distance(lowerSkill, knownSkill.toLowerCase());
			const maxLen = Math.max(lowerSkill.length, knownSkill.length);
			const similarity = 1 - (dist / maxLen);
			
			if (
				dist <= FUZZY_MATCH_THRESHOLD &&
				similarity >= SIMILARITY_THRESHOLD &&
				dist < bestDistance
			) {
				bestMatch = knownSkill;
				bestDistance = dist;
				bestSimilarity = similarity;
			}
		}
		
		if (bestDistance <= FUZZY_MATCH_THRESHOLD && bestSimilarity >= SIMILARITY_THRESHOLD) {
			normalizationCache.set(lowerSkill, bestMatch);
			return bestMatch;
		}
	}
	
	// Default: title case (matching PostgreSQL initcap())
	const normalized = trimmed
		.split(' ')
		.map(word => {
			// Preserve all-caps acronyms (2-4 letters)
			if (word.length >= 2 && word.length <= 4 && word === word.toUpperCase()) {
				return word;
			}
			return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
		})
		.join(' ');
	
	normalizationCache.set(lowerSkill, normalized);
	return normalized;
};

/**
 * Normalizes an array of skills and removes duplicates using fuzzy matching
 * 
 * @param skills - Array of skill names
 * @returns Array of normalized unique skills
 */
export const normalizeSkills = (skills: string[]): string[] => {
	if (!Array.isArray(skills)) return [];
	
	// First pass: normalize using canonical list
	const firstPassNormalized = skills
		.filter(skill => skill && typeof skill === 'string' && skill.trim())
		.map(skill => normalizeSkill(skill.trim()));
	
	const uniqueSkills = Array.from(new Set(firstPassNormalized));
	
	// Second pass: fuzzy matching to consolidate similar skills
	const consolidatedSkills = new Set<string>();
	const processed = new Set<string>();
	
	for (const skill of uniqueSkills) {
		if (processed.has(skill.toLowerCase())) {
			continue;
		}
		
		let found = false;
		for (const existing of consolidatedSkills) {
			const dist = distance(skill.toLowerCase(), existing.toLowerCase());
			const maxLen = Math.max(skill.length, existing.length);
			const similarity = 1 - (dist / maxLen);
			
			if (dist <= FUZZY_MATCH_THRESHOLD && similarity >= SIMILARITY_THRESHOLD) {
				processed.add(skill.toLowerCase());
				found = true;
				break;
			}
		}
		
		if (!found) {
			consolidatedSkills.add(skill);
			processed.add(skill.toLowerCase());
		}
	}
	
	return Array.from(consolidatedSkills).sort();
};
